import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatPhoneInput } from "@/lib/phone";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Upload, X, Save } from "lucide-react";

const EMPTY = { name: "", address: "", phone: "", email: "", website: "", ein: "" };

export default function AdminCompany() {
  const [form, setForm] = useState(EMPTY);
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/company")
      .then(({ data }) => {
        setForm({ ...EMPTY, ...data });
        setLogoUrl(data?.logo_url || null);
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/company", form);
      toast.success("Company profile saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", f);
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.logo_url);
      toast.success("Company logo updated");
    } catch (err) { toast.error(formatApiError(err)); } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="admin-company-page">
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">My Company</h1>
          <p className="text-muted-foreground text-sm">Profile shown on labels, reports, and customer notifications.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-1 p-5 rounded-2xl border-border bg-card/80">
          <Label>Company logo</Label>
          <div className="mt-2 aspect-square rounded-xl border-2 border-dashed border-border bg-secondary/30 flex items-center justify-center overflow-hidden relative">
            {logoUrl ? (
              <img src={logoUrl} alt="Company logo" className="h-full w-full object-contain" data-testid="company-logo-img" />
            ) : (
              <div className="flex flex-col items-center text-muted-foreground text-sm">
                <Building2 className="h-10 w-10" /> <span className="mt-2">No logo yet</span>
              </div>
            )}
            {logoBusy && (
              <div className="absolute inset-0 bg-background/70 grid place-items-center text-sm">Uploading…</div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="sr-only" onChange={onLogo} data-testid="company-logo-input" />
          <div className="mt-3 flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => fileRef.current?.click()} disabled={logoBusy} data-testid="company-logo-pick">
              <Upload className="h-4 w-4 mr-1.5" /> {logoUrl ? "Replace logo" : "Upload logo"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">PNG / JPG. Max 5MB. Square works best.</p>
        </Card>

        <Card className="md:col-span-2 p-5 rounded-2xl border-border bg-card/80 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="company-name">Company name</Label>
              <Input id="company-name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="company-name" placeholder="SRCI Pharm-Route, LLC" />
            </div>
            <div>
              <Label htmlFor="company-ein">EIN #</Label>
              <Input id="company-ein" value={form.ein} onChange={(e) => set("ein", e.target.value)} data-testid="company-ein" placeholder="XX-XXXXXXX" />
            </div>
          </div>
          <div>
            <Label htmlFor="company-address">Address</Label>
            <Input id="company-address" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="company-address" placeholder="123 Main St, Miami, FL 33131" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="company-phone">Phone</Label>
              <Input id="company-phone" value={form.phone} onChange={(e) => set("phone", formatPhoneInput(e.target.value))} data-testid="company-phone" placeholder="305-555-1234" />
            </div>
            <div>
              <Label htmlFor="company-email">Email</Label>
              <Input id="company-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="company-email" placeholder="ops@example.com" />
            </div>
          </div>
          <div>
            <Label htmlFor="company-website">Website</Label>
            <Input id="company-website" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="company-website" placeholder="https://example.com" />
          </div>
          <div className="pt-2 flex justify-end">
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="company-save">
              <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
